import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import AIProviderModal from "../AI/AIProviderModal";
import { aiFetch, providerIcon } from "../AI/aiApi";
import {
  AI_PROVIDERS_ENDPOINT,
  STRIPE_CONNECT_ENDPOINT,
  STRIPE_DISCONNECT_ENDPOINT,
  STRIPE_STATUS_ENDPOINT,
  TENANT_DISABLE_INTEGRATION,
  TENANT_ENABLE_INTEGRATION,
  TENANT_INTEGRATIONS,
  WOOCOMMERCE_CONNECT_ENDPOINT,
  WOOCOMMERCE_DISCONNECT_ENDPOINT,
  WOOCOMMERCE_STATUS_ENDPOINT,
} from "../../config/urls";

const integrations = [
  // Scheduling
  { key: "calendly", name: "Calendly", icon: "📅", componentKey: "calendly", category: "Scheduling" },
  { key: "google_calendar", name: "Google Calendar", icon: "📅", category: "Scheduling" },

  // CRM
  { key: "zoho", name: "Zoho CRM", icon: "🧩", componentKey: "zoho", category: "CRM" },
  { key: "hubspot", name: "HubSpot", icon: "🟠", category: "CRM", comingSoon: true },
  { key: "salesforce", name: "Salesforce", icon: "☁️", category: "CRM", comingSoon: true },
  { key: "freshworks_crm", name: "Freshworks CRM", icon: "🍃", category: "CRM", comingSoon: true },
  { key: "zoho_bigin_crm", name: "Zoho Bigin CRM", icon: "🧩", category: "CRM", comingSoon: true },

  // Payments
  { key: "cashfree", name: "Cashfree", icon: "💸", category: "Payments" },
  { key: "whatsapp_pay", name: "WhatsApp Pay", icon: "💚", category: "Payments", comingSoon: true },
  { key: "razorpay", name: "Razorpay", icon: "💳", category: "Payments", comingSoon: true },
  { key: "payu", name: "PayU", icon: "💰", category: "Payments", comingSoon: true },
  { key: "aspire", name: "Aspire", icon: "💎", category: "Payments", comingSoon: true },
  { key: "xendit", name: "Xendit", icon: "🏦", category: "Payments", comingSoon: true },
  { key: "stripe", name: "Stripe", icon: "🟣", category: "Payments" },

  // E-commerce
  { key: "shopify", name: "Shopify", icon: "🏪", category: "E-commerce", comingSoon: true },
  { key: "shopify_sales", name: "Shopify Sales Channel", icon: "🛍️", category: "E-commerce", comingSoon: true },
  { key: "shopify_marketing", name: "Shopify Marketing", icon: "🛒", category: "E-commerce", comingSoon: true },
  { key: "woocommerce", name: "WooCommerce", icon: "🛒", category: "E-commerce" },
  { key: "yampi", name: "Yampi", icon: "🛍️", category: "E-commerce", comingSoon: true },
  { key: "judge_me", name: "Judge.me | Product Reviews", icon: "⭐", category: "E-commerce", comingSoon: true },

  // Marketing
  { key: "facebook_lead", name: "Facebook Lead Form", icon: "📘", category: "Marketing", comingSoon: true },
  { key: "webengage", name: "WebEngage", icon: "📣", category: "Marketing", comingSoon: true },
  { key: "moengage", name: "MoEngage", icon: "📱", category: "Marketing", comingSoon: true },
  { key: "clevertap", name: "CleverTap", icon: "🔔", category: "Marketing", comingSoon: true },

  // Automation
  { key: "zapier", name: "Zapier", icon: "⚡", category: "Automation", comingSoon: true },
  { key: "integromat", name: "Integromat", icon: "⚙️", category: "Automation", comingSoon: true },
  { key: "pabbly_connect", name: "Pabbly Connect", icon: "🔗", category: "Automation", comingSoon: true },

  // Productivity
  { key: "google_sheets", name: "Google Sheets", icon: "📊", category: "Productivity", comingSoon: true },

  // Billing
  { key: "zoho_billing", name: "Zoho Billing", icon: "🧾", category: "Billing", comingSoon: true },
  { key: "zoho_books", name: "Zoho Books", icon: "📚", category: "Billing", comingSoon: true },

  // Helpdesk
  { key: "freshdesk_v2", name: "Freshdesk v2", icon: "🎧", category: "Helpdesk", comingSoon: true },

  // Other
  { key: "wafegq", name: "Wafegq", icon: "🔧", category: "Other", comingSoon: true },
];

// AI provider cards come from the backend catalog (GET /integrations/ai/providers/),
// so providers are added or retired there, not in this list.
const AI_CATEGORY = "AI";
const CATEGORIES = ["All", AI_CATEGORY, ...Array.from(new Set(integrations.map((i) => i.category)))];

const IntegrationsOverview = () => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();

  const [saving, setSaving] = useState(false);
  const [enabledIntegrations, setEnabledIntegrations] = useState([]);

  // Search & filter
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  // Modal States
  const [googleModalOpen, setGoogleModalOpen] = useState(false);
  const [cashfreeModalOpen, setCashfreeModalOpen] = useState(false);
  const [wooCommerceModalOpen, setWooCommerceModalOpen] = useState(false);

  // Cashfree Credential States
  const [cashfreeAppId, setCashfreeAppId] = useState("");
  const [cashfreeSecretKey, setCashfreeSecretKey] = useState("");

  // WooCommerce Connection State
  const [wooCommerceStatus, setWooCommerceStatus] = useState({ connected: false });
  const [wooCommerceSiteUrl, setWooCommerceSiteUrl] = useState("");
  const [wooCommerceConsumerKey, setWooCommerceConsumerKey] = useState("");
  const [wooCommerceConsumerSecret, setWooCommerceConsumerSecret] = useState("");
  const [wooCommerceSaving, setWooCommerceSaving] = useState(false);
  const [wooCommerceError, setWooCommerceError] = useState("");

  // Stripe Connection State
  const [stripeModalOpen, setStripeModalOpen] = useState(false);
  const [stripeStatus, setStripeStatus] = useState({ connected: false });
  const [stripeSecretKey, setStripeSecretKey] = useState("");
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  const [stripeSaving, setStripeSaving] = useState(false);
  const [stripeError, setStripeError] = useState("");

  // AI providers (tenant's own keys) — catalog-driven, one generic modal
  const [aiProviders, setAiProviders] = useState([]);
  const [aiModalSlug, setAiModalSlug] = useState(null);

  const fetchAiProviders = async () => {
    const res = await aiFetch(getValidToken, AI_PROVIDERS_ENDPOINT);
    if (res.ok) setAiProviders(res.data.providers || []);
  };

  useEffect(() => {
    fetchAiProviders();
  }, []);

  const handleAiProviderChanged = (updated) => {
    setAiProviders((prev) => prev.map((p) => (p.slug === updated.slug ? updated : p)));
  };

  const aiCards = aiProviders.map((p) => ({
    key: `ai_${p.slug}`,
    name: p.name,
    icon: providerIcon(p.slug),
    category: AI_CATEGORY,
    aiProvider: p,
  }));

  /* ---------------- Fetch WooCommerce connection status ---------------- */
  /* WooCommerce is stored via the dedicated integrations app (credential
     storage, connection audit log), not the legacy Tenant.integrations
     JSONField the other tiles below use — so its connected state is tracked
     separately from enabledIntegrations. */

  const fetchWooCommerceStatus = async () => {
    const token = await getValidToken();

    try {
      const response = await fetch(WOOCOMMERCE_STATUS_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (!response.ok) {
        console.error("Failed to fetch WooCommerce status");
        return;
      }

      const data = await response.json();
      setWooCommerceStatus(data);
    } catch (error) {
      console.error("Failed to fetch WooCommerce status", error);
    }
  };

  useEffect(() => {
    fetchWooCommerceStatus();
  }, []);

  const connectWooCommerce = async () => {
    const token = await getValidToken();

    try {
      setWooCommerceSaving(true);
      setWooCommerceError("");

      const response = await fetch(WOOCOMMERCE_CONNECT_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
        body: JSON.stringify({
          site_url: wooCommerceSiteUrl,
          consumer_key: wooCommerceConsumerKey,
          consumer_secret: wooCommerceConsumerSecret,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to connect WooCommerce");
      }

      setWooCommerceStatus({ connected: true, site_url: data.site_url });
      setWooCommerceModalOpen(false);
      setWooCommerceConsumerKey("");
      setWooCommerceConsumerSecret("");
    } catch (err) {
      setWooCommerceError(err.message);
    } finally {
      setWooCommerceSaving(false);
    }
  };

  const disconnectWooCommerce = async () => {
    const token = await getValidToken();

    try {
      setWooCommerceSaving(true);
      setWooCommerceError("");

      const response = await fetch(WOOCOMMERCE_DISCONNECT_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to disconnect WooCommerce");
      }

      setWooCommerceStatus({ connected: false });
      setWooCommerceModalOpen(false);
      setWooCommerceSiteUrl("");
    } catch (err) {
      setWooCommerceError(err.message);
    } finally {
      setWooCommerceSaving(false);
    }
  };

  /* ---------------- Fetch Stripe connection status ---------------- */
  /* Stripe is stored via the dedicated integrations app (credential
     storage, connection audit log), same as WooCommerce above — not the
     legacy Tenant.integrations JSONField the other tiles below use. */

  const fetchStripeStatus = async () => {
    const token = await getValidToken();

    try {
      const response = await fetch(STRIPE_STATUS_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (!response.ok) {
        console.error("Failed to fetch Stripe status");
        return;
      }

      const data = await response.json();
      setStripeStatus(data);
    } catch (error) {
      console.error("Failed to fetch Stripe status", error);
    }
  };

  useEffect(() => {
    fetchStripeStatus();
  }, []);

  const connectStripe = async () => {
    const token = await getValidToken();

    try {
      setStripeSaving(true);
      setStripeError("");

      const response = await fetch(STRIPE_CONNECT_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
        body: JSON.stringify({
          secret_key: stripeSecretKey,
          publishable_key: stripePublishableKey,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to connect Stripe");
      }

      setStripeStatus({
        connected: true,
        account_id: data.account_id,
        mode: data.mode,
        secret_key_display: data.secret_key_display,
        webhook_url: data.webhook_url,
        webhook_registered: data.webhook_registered,
        webhook_registration_error: data.webhook_registration_error,
      });
      setStripeSecretKey("");
      setStripePublishableKey("");

      if (data.webhook_registered) {
        setStripeModalOpen(false);
      } else {
        // Account connected fine, but the webhook (needed for payment
        // status updates) failed to register — keep the modal open so this
        // isn't missed, since it otherwise only surfaces later in the
        // Payment Link node's config panel.
        setStripeError(
          `Connected, but webhook registration failed: ${data.webhook_registration_error || "unknown error"}. Payment status updates won't arrive until this is fixed — disconnect and connect again to retry.`,
        );
      }
    } catch (err) {
      setStripeError(err.message);
    } finally {
      setStripeSaving(false);
    }
  };

  const disconnectStripe = async () => {
    const token = await getValidToken();

    try {
      setStripeSaving(true);
      setStripeError("");

      const response = await fetch(STRIPE_DISCONNECT_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to disconnect Stripe");
      }

      setStripeStatus({ connected: false });
      setStripeModalOpen(false);
    } catch (err) {
      setStripeError(err.message);
    } finally {
      setStripeSaving(false);
    }
  };

  /* ---------------- Fetch enabled integrations ---------------- */

  const fetchIntegrations = async () => {
    const token = await getValidToken();
    const endpoint = TENANT_INTEGRATIONS;

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const response = await fetch(endpoint, { headers });

      if (!response.ok) {
        console.error("Failed to fetch integrations");
        return;
      }

      const data = await response.json();
      setEnabledIntegrations(data.integrations || []);
    } catch (error) {
      console.error("Failed to fetch integrations", error);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  /* ---------------- Click handling ---------------- */

  const handleIntegrationClick = async (integration) => {
    if (integration.aiProvider) {
      setAiModalSlug(integration.aiProvider.slug);
      return;
    }

    if (integration.key === "google_calendar") {
      setGoogleModalOpen(true);
      return;
    }

    if (integration.key === "cashfree") {
      setCashfreeModalOpen(true);
      return;
    }

    if (integration.key === "woocommerce") {
      setWooCommerceSiteUrl(wooCommerceStatus?.site_url || "");
      setWooCommerceError("");
      setWooCommerceModalOpen(true);
      return;
    }

    if (integration.key === "stripe") {
      setStripeError("");
      setStripeModalOpen(true);
      return;
    }

    if (!integration.componentKey) return;

    localStorage.setItem("activePage", integration.componentKey);
    window.location.href = "/";
  };

  /* ---------------- Enable / Disable ---------------- */

  const toggleIntegration = async (integrationKey, settings = null) => {
    const token = await getValidToken();
    const isEnabled = enabledIntegrations.includes(integrationKey);

    const endpoint = isEnabled
      ? TENANT_DISABLE_INTEGRATION
      : TENANT_ENABLE_INTEGRATION;

    try {
      setSaving(true);

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const body = { integration: integrationKey };

      if (settings) {
        body.settings = settings;
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to update ${integrationKey} integration`);
      }

      const data = await response.json();
      setEnabledIntegrations(data.integrations);
      setGoogleModalOpen(false);
      setCashfreeModalOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- Filtering ---------------- */

  const filteredIntegrations = [...aiCards, ...integrations].filter((integration) => {
    const matchesCategory =
      activeCategory === "All" || integration.category === activeCategory;
    const matchesSearch = integration.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const isGoogleEnabled = enabledIntegrations.includes("google_calendar");
  const isCashfreeEnabled = enabledIntegrations.includes("cashfree");

  return (
    <div style={{ padding: "2rem" }}>
      <h2 className="heading-title" style={{ marginBottom: "1.5rem" }}>
        🔌 Available Integrations
      </h2>

      {/* Search */}
      <TextField
        placeholder="Search integrations..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        size="small"
        sx={{ mb: 2, width: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Category filters */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
        {CATEGORIES.map((cat) => (
          <Chip
            key={cat}
            label={cat}
            clickable
            onClick={() => setActiveCategory(cat)}
            color={activeCategory === cat ? "primary" : "default"}
            variant={activeCategory === cat ? "filled" : "outlined"}
            size="small"
          />
        ))}
      </Box>

      {/* Integration cards */}
      <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
        {filteredIntegrations.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            No integrations found.
          </Typography>
        ) : (
          filteredIntegrations.map((integration) => {
            const aiConnection = integration.aiProvider?.connection;
            const aiError = aiConnection?.status === "error";
            const enabled = integration.aiProvider
              ? !!aiConnection && !aiError
              : integration.key === "woocommerce"
                ? wooCommerceStatus?.connected
                : integration.key === "stripe"
                  ? stripeStatus?.connected
                  : enabledIntegrations.includes(integration.key);

            return (
              <div
                key={integration.key}
                onClick={() => handleIntegrationClick(integration)}
                style={{
                  cursor: "pointer",
                  width: "180px",
                  padding: "1.25rem",
                  backgroundColor: "#f9f9f9",
                  border: "1px solid #ddd",
                  borderRadius: "12px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                  textAlign: "center",
                  transition: "0.3s",
                }}
              >
                <div style={{ fontSize: "2.2rem" }}>{integration.icon}</div>
                <h3
                  style={{
                    marginTop: "0.75rem",
                    fontWeight: "600",
                    fontSize: "0.85rem",
                    lineHeight: 1.3,
                  }}
                >
                  {integration.name}
                </h3>
                <Typography
                  variant="caption"
                  sx={{ color: "text.disabled", display: "block", mb: 0.5 }}
                >
                  {integration.category}
                </Typography>
                {enabled && (
                  <Typography variant="caption" sx={{ color: "green", fontWeight: 600 }}>
                    {integration.aiProvider ? "Connected" : "Enabled"}
                  </Typography>
                )}
                {aiError && (
                  <Typography variant="caption" sx={{ color: "error.main", fontWeight: 600 }}>
                    Key error
                  </Typography>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ---------------- Google Calendar Modal ---------------- */}
      <Modal open={googleModalOpen} onClose={() => setGoogleModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {isGoogleEnabled ? "Disable Google Calendar?" : "Enable Google Calendar?"}
            </Typography>
            <IconButton onClick={() => setGoogleModalOpen(false)} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Divider />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              {isGoogleEnabled
                ? "This will disconnect Google Calendar from your account."
                : "This will allow appointment sync with Google Calendar."}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              onClick={() => toggleIntegration("google_calendar")}
              disabled={saving}
              color={isGoogleEnabled ? "error" : "primary"}
            >
              {saving ? (
                <CircularProgress size={20} />
              ) : isGoogleEnabled ? (
                "Disable"
              ) : (
                "Enable"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ---------------- Cashfree Modal ---------------- */}
      <Modal open={cashfreeModalOpen} onClose={() => setCashfreeModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {isCashfreeEnabled ? "Disable Cashfree?" : "Enable Cashfree?"}
            </Typography>
            <IconButton onClick={() => setCashfreeModalOpen(false)} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Divider />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              {isCashfreeEnabled
                ? "This will disconnect Cashfree Payment Gateway from your account."
                : "This will allow payment processing with Cashfree Payment Gateway."}
            </Typography>
          </Box>

          {!isCashfreeEnabled && (
            <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth
                label="Cashfree App ID"
                value={cashfreeAppId}
                onChange={(e) => setCashfreeAppId(e.target.value)}
                required="true"
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                }}
              />
              <TextField
                fullWidth
                label="Cashfree Secret Key"
                value={cashfreeSecretKey}
                onChange={(e) => setCashfreeSecretKey(e.target.value)}
                required="true"
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                }}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              onClick={() =>
                toggleIntegration("cashfree", {
                  cashfree_app_id: cashfreeAppId,
                  cashfree_secret_key: cashfreeSecretKey,
                })
              }
              disabled={
                saving ||
                (!isCashfreeEnabled && (!cashfreeAppId || !cashfreeSecretKey))
              }
              color={isCashfreeEnabled ? "error" : "primary"}
            >
              {saving ? (
                <CircularProgress size={20} />
              ) : isCashfreeEnabled ? (
                "Disable"
              ) : (
                "Enable"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ---------------- WooCommerce Modal ---------------- */}
      <Modal open={wooCommerceModalOpen} onClose={() => setWooCommerceModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {wooCommerceStatus?.connected ? "WooCommerce Connected" : "Connect WooCommerce"}
            </Typography>
            <IconButton onClick={() => setWooCommerceModalOpen(false)} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Divider />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              {wooCommerceStatus?.connected
                ? `This tenant's WooCommerce store (${wooCommerceStatus.site_url}) is connected. Shipping charge calculation nodes set to "WooCommerce" will use its live shipping rates.`
                : "Connect your WooCommerce store's REST API credentials to enable accurate shipping charge calculation and order data lookups from Sales Nodebot."}
            </Typography>
          </Box>

          {!wooCommerceStatus?.connected && (
            <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth
                label="Store URL"
                placeholder="https://yourstore.com"
                value={wooCommerceSiteUrl}
                onChange={(e) => setWooCommerceSiteUrl(e.target.value)}
                required
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                }}
              />
              <TextField
                fullWidth
                label="Consumer Key"
                value={wooCommerceConsumerKey}
                onChange={(e) => setWooCommerceConsumerKey(e.target.value)}
                required
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                }}
              />
              <TextField
                fullWidth
                type="password"
                label="Consumer Secret"
                value={wooCommerceConsumerSecret}
                onChange={(e) => setWooCommerceConsumerSecret(e.target.value)}
                required
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                }}
              />
            </Box>
          )}

          {wooCommerceError && (
            <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 2 }}>
              {wooCommerceError}
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              onClick={wooCommerceStatus?.connected ? disconnectWooCommerce : connectWooCommerce}
              disabled={
                wooCommerceSaving ||
                (!wooCommerceStatus?.connected &&
                  (!wooCommerceSiteUrl || !wooCommerceConsumerKey || !wooCommerceConsumerSecret))
              }
              color={wooCommerceStatus?.connected ? "error" : "primary"}
            >
              {wooCommerceSaving ? (
                <CircularProgress size={20} />
              ) : wooCommerceStatus?.connected ? (
                "Disconnect"
              ) : (
                "Connect"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ---------------- AI Provider Modal ---------------- */}
      <AIProviderModal
        provider={aiProviders.find((p) => p.slug === aiModalSlug)}
        open={!!aiModalSlug}
        onClose={() => setAiModalSlug(null)}
        onChanged={handleAiProviderChanged}
      />

      {/* ---------------- Stripe Modal ---------------- */}
      <Modal open={stripeModalOpen} onClose={() => setStripeModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {stripeStatus?.connected ? "Stripe Connected" : "Connect Stripe"}
            </Typography>
            <IconButton onClick={() => setStripeModalOpen(false)} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Divider />

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              {stripeStatus?.connected
                ? `Connected to Stripe account ${stripeStatus.account_id} (${stripeStatus.mode === "live" ? "Live" : "Test"} mode, key ${stripeStatus.secret_key_display}). Payment Link nodes can now use Stripe.`
                : "Enter your Stripe secret key to enable it as a payment gateway for Payment Link nodes. Your key is verified against Stripe before it's saved."}
            </Typography>
          </Box>

          {!stripeStatus?.connected && (
            <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth
                type="password"
                label="Secret Key"
                placeholder="sk_test_..."
                value={stripeSecretKey}
                onChange={(e) => setStripeSecretKey(e.target.value)}
                required
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                }}
              />
              <TextField
                fullWidth
                label="Publishable Key (optional)"
                placeholder="pk_test_..."
                value={stripePublishableKey}
                onChange={(e) => setStripePublishableKey(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                }}
              />
            </Box>
          )}

          {stripeError && (
            <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 2 }}>
              {stripeError}
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              onClick={stripeStatus?.connected ? disconnectStripe : connectStripe}
              disabled={stripeSaving || (!stripeStatus?.connected && !stripeSecretKey)}
              color={stripeStatus?.connected ? "error" : "primary"}
            >
              {stripeSaving ? (
                <CircularProgress size={20} />
              ) : stripeStatus?.connected ? (
                "Disconnect"
              ) : (
                "Connect"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default IntegrationsOverview;
